import { Request } from '../codegen/graphql';
import { calculateMargin } from './calculateMargin';
import { getRequestEffectiveCostPrice } from './requestZonePricing';

export function getRequestHighestCostPrice(request: Request): number | null {
    return getRequestEffectiveCostPrice(request);
}

export type ProposedMarginIndicatorModel = {
    marginValue: number;
    isMarginPositive: boolean;
};

export function getInterimPriceForRequest(
    request: Request,
): number | undefined {
    const { store } = request;
    const storeNo = store?.storeNo ?? request.storeNo ?? undefined;
    const isTradingAsCashbuild = store?.isTradingAsCashbuild ?? false;
    const cashbuild = request.temporaryCashbuildPrice;
    const pnl = request.temporaryPnlPrice;
    const changed = request.temporaryChangedPrice;

    if (storeNo?.startsWith('P')) {
        if (isTradingAsCashbuild) {
            if (
                cashbuild != null &&
                Number.isFinite(cashbuild) &&
                cashbuild > 0
            ) {
                return cashbuild;
            }
        } else if (pnl != null && Number.isFinite(pnl) && pnl > 0) {
            return pnl;
        }
        if (changed != null && Number.isFinite(changed) && changed > 0) {
            return changed;
        }
        return undefined;
    }
    if (cashbuild != null && Number.isFinite(cashbuild) && cashbuild > 0) {
        return cashbuild;
    }
    if (changed != null && Number.isFinite(changed) && changed > 0) {
        return changed;
    }
    return undefined;
}

export function getQuickActionNewPriceSeed(
    request: Request,
): number | undefined {
    const interim = getInterimPriceForRequest(request);
    if (interim != null && interim > 0) {
        return interim;
    }
    const proposed = request.proposedPrice;
    if (
        typeof proposed === 'number' &&
        Number.isFinite(proposed) &&
        proposed > 0
    ) {
        return proposed;
    }
    return undefined;
}

export function getProposedMarginIndicatorModel(
    request: Request & { resolvedVatPercentage?: number | null },
    fallbackVatPercentage: number,
): ProposedMarginIndicatorModel {
    const benchmarkMargin = request.benchmarkMargin ?? 0;
    const requestHighestCostPrice = getRequestHighestCostPrice(request);

    if (requestHighestCostPrice != null && requestHighestCostPrice > 0) {
        const marginCalculationResult = calculateMargin({
            costPrice: requestHighestCostPrice,
            currentPrice: request.currentPrice ?? 0,
            proposedPrice: request.proposedPrice ?? 0,
            benchmarkMargin,
            vatPercentage:
                request.resolvedVatPercentage ?? fallbackVatPercentage,
        });
        return {
            marginValue: marginCalculationResult.newMargin,
            isMarginPositive: marginCalculationResult.isMarginPositive,
        };
    }

    const proposedMarginFromRequest = request.proposedMargin ?? 0;
    return {
        marginValue: proposedMarginFromRequest,
        isMarginPositive: proposedMarginFromRequest >= benchmarkMargin,
    };
}
